import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { filterMovies, paginate, sortMovies, type SortKey } from "@/utils/movie";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import type { ContentType } from "@/types/movie";

const PAGE_SIZE = 12;

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "release", label: "按上映时间" },
  { value: "hot", label: "按热度" },
  { value: "rating", label: "按评分" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const type = (searchParams.get("type") || "all") as ContentType | "all";
  const genre = searchParams.get("genre") || "all";
  const region = searchParams.get("region") || "all";
  const year = searchParams.get("year") || "all";
  const sort = (searchParams.get("sort") || "release") as SortKey;
  const page = Number(searchParams.get("page") || "1");

  const title = type === "movie" ? "电影分类" : type === "tv" ? "电视剧分类" : "影视分类";

  useDocumentTitle(
    `${title} - 色色影院`,
    `浏览色色影院海量${type === "tv" ? "电视剧" : type === "movie" ? "电影" : "影视"}资源，按类型、地区、年份筛选精准查找。`,
  );

  const updateParams = (changes: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (!value || value === "all") next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const filtered = useMemo(
    () => filterMovies({ type, genre, region, year: year === "all" ? "all" : Number(year) }),
    [type, genre, region, year],
  );

  const sorted = useMemo(() => sortMovies(filtered, sort), [filtered, sort]);
  const { items, totalPages, page: safePage } = paginate(sorted, page, PAGE_SIZE);

  const resetAll = () => setSearchParams(new URLSearchParams());

  const filterValue: FilterState = { genre, region, year };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 md:py-8">
        <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="transition-colors hover:text-primary-700">
            分类
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{title}</span>
        </nav>

        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            共收录 <span className="font-semibold text-accent-600">{sorted.length}</span> 部作品，可通过类型、地区、年份组合筛选精准查找。
          </p>
        </div>

        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { value: "all", label: "全部" },
              { value: "movie", label: "电影" },
              { value: "tv", label: "电视剧" },
            ].map((tab) => (
              <Link
                key={tab.value}
                to={tab.value === "all" ? "/category" : `/category?type=${tab.value}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  type === tab.value
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            <div className="flex rounded-full bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateParams({ sort: option.value })}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    sort === option.value
                      ? "bg-background-50 text-primary-700"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <FilterPanel value={filterValue} onChange={(next) => updateParams(next as Record<string, string>)} />

        {items.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
              {items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onChange={(next) => updateParams({ page: String(next) }, false)}
            />
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground-900">
              没有符合条件的影视
            </h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              试试放宽筛选条件，或清除所有筛选重新浏览全部内容。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line text-lg"></i>
              重置筛选
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}